import { useState, useEffect, useRef } from "react";
import { NavLink } from "../../../../../../types/drawer/NavItem";
import Icon, { icons } from "../icon/Icon";
import { Link, useLocation } from "react-router-dom";

interface SubMenuProps {
  isOpen: boolean;
  isDropdownOpen: boolean;
  link: NavLink;
}

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  show: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, show }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [isHovered]);

  return (
    <div 
      ref={ref}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {show && isHovered && (
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{ 
            top: position.top,
            left: position.left,
            transform: 'translateY(-50%)'
          }}
        >
          <div className="bg-[#1a2744] text-[#DDEBFF] text-[13px] font-[300] px-3 py-1.5 rounded-md whitespace-nowrap shadow-lg border border-[#2a3a5c]">
            {text}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SubMenu({
  isOpen,
  isDropdownOpen,
  link,
}: SubMenuProps) {
  const location = useLocation();

  return (
    <div>
      {isDropdownOpen && (
        <div
          className={`my-[8px] ${
            isOpen &&
            "border-l-[1px] ml-[18px] !my-[20px] border-[#6B96D633] pl-[18px]"
          }`}
        >
          {link?.children?.map((category, catIndex) => (
            <div key={catIndex} className={catIndex === 0 ? "" : "mt-[20px]"}>
              {(category.category || (!isOpen && category.icon)) && (
                <Tooltip text={category.category} show={!isOpen}>
                  <p
                    className={`text-[10px] ${
                      !isOpen &&
                      "flex items-center group py-2 px-3 text-[10px] font-[500] transition-all duration-300 text-[#dae8fd] hover:bg-[#0193f8] hover:text-[#042849] justify-center h-[45px] w-[45px] mx-auto rounded-[10px]"
                    } mb-[10px] uppercase text-[#6B96D6]`}
                  >
                    {!isOpen && (
                      <Icon
                        isOpen={isOpen}
                        category={true}
                        isActive={false}
                        name={category.icon as keyof typeof icons}
                      />
                    )}
                    {isOpen && <span>{category.category}</span>}
                  </p>
                </Tooltip>
              )}

              {category.links.map((sublink, subIndex) => {
                const isSubLinkActive = location.pathname === sublink.href;

                return (
                  <Tooltip key={subIndex} text={sublink.text} show={!isOpen}>
                    <Link
                      to={sublink.href}
                      className={`flex items-center h-[40px] px-3 ${
                        !isOpen &&
                        "group my-[5px] text-[16px] transition-all duration-300 text-[#dae8fd] hover:bg-[#0193f8] hover:text-[#042849] justify-center w-[40px] mx-auto rounded-[10px]"
                      } text-[14px] rounded-[12px] ${
                        isSubLinkActive
                          ? `text-[#05050F] bg-[#6B96D6] !font-[400] ${
                              !isOpen && "bg-[#0193f8]"
                            } font-semibold`
                          : "text-[#0193F8] hover:text-[#fff]"
                      }`}
                    >
                      {!isOpen && (
                        <Icon
                          isOpen={isOpen}
                          isActive={isSubLinkActive}
                          name={sublink.icon as keyof typeof icons}
                        />
                      )}
                      {isOpen && <span className="whitespace-nowrap">{sublink.text}</span>}
                    </Link>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
